// src/components/Payroll/VariableIncomesModule/VariableIncomesModule.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Save, TrendingUp, CheckCircle, ArrowLeft, Clock, AlertCircle, Lock } from 'lucide-react';
import {
  HorasExtrasSection,
  ComisionesSection,
  VacacionesSection,
  IncentivosSection,
  BonificacionesSection,
  OtrosIngresosSection
} from './sections';
import { useVariableIncomes } from '../../../hooks/useVariableIncomes';
import { getEmployeeBalance, calcVacationPay } from '../../../api/vacations';

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });

const INCOME_ROWS = [
  { key: 'horas_extras',   label: 'Horas Extras'   },
  { key: 'comisiones',     label: 'Comisiones'     },
  { key: 'vacaciones',     label: 'Vacaciones'     },
  { key: 'incentivos',     label: 'Incentivos'     },
  { key: 'bonificaciones', label: 'Bono por Desempeño' },
  { key: 'otros_ingresos', label: 'Otros Ingresos' },
];

/**
 * Módulo de Ingresos Variables — vista de detalle por empleado
 * ✅ FIX: Loop infinito corregido con useCallback
 */
export default function VariableIncomesModule({
  employeeId,
  employeeName,
  employeeIdDisplay,
  cargo,
  periodo,
  quincena,
  salarioBase,
  onSave,
  onCancel
}) {
  const { getVariableIncomes, saveVariableIncomes, loading } = useVariableIncomes();

  const [incomes, setIncomes] = useState({
    horas_extras:   0,
    comisiones:     0,
    vacaciones:     0,
    incentivos:     0,
    bonificaciones: 0,
    otros_ingresos: 0,
  });

  const [detalles, setDetalles] = useState({
    horasExtrasDetalle:     null,
    comisionesDetalle:      [],
    vacacionesDetalle:      null,
    incentivosDetalle:      null,
    bonificacionesDetalle:  null,
    otrosIngresosDetalle:   null,
  });

  const [saving,        setSaving]        = useState(false);
  const [showSuccess,   setShowSuccess]   = useState(false);
  const [estado,        setEstado]        = useState(null);   // pendiente | aprobado | rechazado | null
  const [motivoRechazo, setMotivoRechazo] = useState(null);
  const [loadingVac,    setLoadingVac]    = useState(false);
  const [vacInfo,       setVacInfo]       = useState(null);

  // ✅ FIX: useCallback para evitar recrear la función
  const loadExistingIncomes = useCallback(async () => {
    if (!employeeId || !periodo || !quincena) return;
    try {
      const data = await getVariableIncomes(employeeId, periodo, quincena);
      if (data) {
        setIncomes({
          horas_extras:   parseFloat(data.horas_extras   || 0),
          comisiones:     parseFloat(data.comisiones     || 0),
          vacaciones:     parseFloat(data.vacaciones     || 0),
          incentivos:     parseFloat(data.incentivos     || 0),
          bonificaciones: parseFloat(data.bonificaciones || 0),
          otros_ingresos: parseFloat(data.otros_ingresos || 0),
        });
        setEstado(data.estado || null);
        setMotivoRechazo(data.motivo_rechazo || null);
        const parsed = typeof data.detalles === 'string'
          ? JSON.parse(data.detalles)
          : data.detalles;
        setDetalles(parsed || {
          horasExtrasDetalle:    null,
          comisionesDetalle:     [],
          vacacionesDetalle:     null,
          incentivosDetalle:     null,
          bonificacionesDetalle: null,
          otrosIngresosDetalle:  null,
        });
      } else {
        setEstado(null);
        setMotivoRechazo(null);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error loading variable incomes:', error);
      }
    }
  }, [employeeId, periodo, quincena, getVariableIncomes]);

  // ✅ FIX: useEffect con dependencias correctas
  useEffect(() => {
    loadExistingIncomes();
  }, [loadExistingIncomes]);

  const handleUpdateIncome = (type, amount, detalle = null) => {
    setIncomes(prev => ({ ...prev, [type]: parseFloat(amount || 0) }));
    if (detalle !== null) {
      setDetalles(prev => ({ ...prev, [`${type}Detalle`]: detalle }));
    }
  };

  const handleLoadVacaciones = async (tipoCalculo) => {
    if (!employeeId || !periodo) return;
    try {
      setLoadingVac(true);
      const year = parseInt(periodo.split('-')[0]);

      // Obtener balance del empleado (días asignados por política y días disponibles)
      const balRes = await getEmployeeBalance(employeeId, year);
      const bal = balRes.data ?? balRes;

      // Elegir cantidad de días según tipo
      let dias;
      if (tipoCalculo === 'diferencia') {
        dias = parseFloat(bal.days_available ?? 0);
        if (dias <= 0) {
          alert('El empleado no tiene días disponibles en su balance de vacaciones.');
          return;
        }
      } else {
        // 'total' y 'prima' usan los días asignados por política (14 o 18)
        dias = parseFloat(bal.days_allocated ?? 0);
        if (dias <= 0) {
          alert('El empleado no tiene días asignados en su balance de vacaciones.');
          return;
        }
      }

      // Calcular monto usando el endpoint del servidor (aplica fórmula actualizada)
      // Se pasa el período actual para que el backend use el salario de la nómina
      // inmediatamente anterior a este mes (evita tomar nóminas futuras o del mismo período)
      const tipoApi = tipoCalculo === 'diferencia' ? 'total' : tipoCalculo;
      const calcRes = await calcVacationPay(employeeId, dias, tipoApi, periodo);
      const calc = calcRes.data ?? calcRes;

      const etiquetas = { total: 'Pago total', prima: 'Prima vacacional', diferencia: 'Días disponibles' };
      const concepto = `${dias} día(s) · ${etiquetas[tipoCalculo]}`;
      const monto = calc.monto;

      handleUpdateIncome('vacaciones', monto, { concepto });
      setVacInfo({ dias, tipo_pago: tipoCalculo, monto, ...calc });
    } catch (e) {
      alert('Error al cargar vacaciones: ' + e.message);
    } finally {
      setLoadingVac(false);
    }
  };

  const totalIngresos = Object.values(incomes).reduce(
    (sum, val) => sum + parseFloat(val || 0), 0
  );

  const handleSave = async () => {
    try {
      setSaving(true);

      const companyId =
        localStorage.getItem('company_id') ||
        localStorage.getItem('companyId') ||
        JSON.parse(localStorage.getItem('user') || '{}').company_id;

      if (!companyId) {
        alert('Error: No se pudo determinar la compañía. Por favor, vuelve a iniciar sesión.');
        return;
      }

      const result = await saveVariableIncomes({
        employeeId,
        companyId,
        periodo,
        quincena,
        horas_extras:   parseFloat(incomes.horas_extras   || 0),
        comisiones:     parseFloat(incomes.comisiones     || 0),
        vacaciones:     parseFloat(incomes.vacaciones     || 0),
        incentivos:     parseFloat(incomes.incentivos     || 0),
        bonificaciones: parseFloat(incomes.bonificaciones || 0),
        otros_ingresos: parseFloat(incomes.otros_ingresos || 0),
        detalles,
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      if (onSave) onSave(result);
    } catch (error) {
      console.error('Error saving variable incomes:', error);
      alert('Error al guardar los ingresos variables: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando ingresos...</p>
        </div>
      </div>
    );
  }

  const isAprobado  = estado === 'aprobado';
  const isRechazado = estado === 'rechazado';
  const isPendiente = estado === 'pendiente';

  const estadoBadge = {
    aprobado:  { label: 'Aprobado',            cls: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle size={12} /> },
    pendiente: { label: 'Pendiente de aprobación', cls: 'bg-amber-100 text-amber-700 border-amber-200',  icon: <Clock size={12} />       },
    rechazado: { label: 'Rechazado',            cls: 'bg-red-100 text-red-700 border-red-200',      icon: <AlertCircle size={12} /> },
  };

  return (
    <div className="space-y-4">

      {/* Success toast */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-5 py-3 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium">
          <CheckCircle size={16} />
          Ingresos guardados — pendiente de aprobación
        </div>
      )}

      {/* ── Banner: rechazado ────────────────────────────────────── */}
      {isRechazado && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Ingreso rechazado.</span>
            {motivoRechazo && <span className="ml-1">Motivo: {motivoRechazo}.</span>}
            <span className="ml-1">Puedes corregir los valores y guardar nuevamente para enviarlo a aprobación.</span>
          </div>
        </div>
      )}

      {/* ── Banner: aprobado (solo lectura) ─────────────────────── */}
      {isAprobado && (
        <div className="flex items-start gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <Lock size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Ingreso aprobado — solo lectura.</span>
            <span className="ml-1">Para realizar cambios, contacta a tu supervisor para que lo rechace y puedas editarlo.</span>
          </div>
        </div>
      )}

      {/* ── Banner: pendiente ────────────────────────────────────── */}
      {isPendiente && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <Clock size={14} className="flex-shrink-0" />
          Este ingreso está pendiente de aprobación. Puedes editarlo mientras no haya sido aprobado.
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onCancel}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {employeeName || 'Empleado'}
              </p>
              {estado && estadoBadge[estado] && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full border ${estadoBadge[estado].cls}`}>
                  {estadoBadge[estado].icon}
                  {estadoBadge[estado].label}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 truncate">
              {[employeeIdDisplay, cargo, periodo, quincena === 'Q1' ? '1ra quincena' : '2da quincena']
                .filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-gray-400">Total variables</p>
            <p className="text-sm font-bold text-gray-900">RD$ {fmt(totalIngresos)}</p>
          </div>
          {!isAprobado && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving
                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                : <Save size={14} />
              }
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          )}
        </div>
      </div>

      {/* ── Sections grid ───────────────────────────────────────── */}
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 ${isAprobado ? 'pointer-events-none opacity-75' : ''}`}>
        <HorasExtrasSection
          value={incomes.horas_extras}
          detalle={detalles.horasExtrasDetalle}
          salarioBase={salarioBase}
          onUpdate={(amt, det) => handleUpdateIncome('horas_extras', amt, det)}
        />
        <ComisionesSection
          value={incomes.comisiones}
          detalle={detalles.comisionesDetalle}
          onUpdate={(amt, det) => handleUpdateIncome('comisiones', amt, det)}
        />
        <VacacionesSection
          value={incomes.vacaciones}
          detalle={detalles.vacacionesDetalle}
          onUpdate={(amt, det) => handleUpdateIncome('vacaciones', amt, det)}
          onLoad={!isAprobado ? handleLoadVacaciones : undefined}
          loadingVac={loadingVac}
          vacInfo={vacInfo}
        />
        <IncentivosSection
          value={incomes.incentivos}
          detalle={detalles.incentivosDetalle}
          onUpdate={(amt, det) => handleUpdateIncome('incentivos', amt, det)}
        />
        <BonificacionesSection
          value={incomes.bonificaciones}
          detalle={detalles.bonificacionesDetalle}
          onUpdate={(amt, det) => handleUpdateIncome('bonificaciones', amt, det)}
        />
        <OtrosIngresosSection
          value={incomes.otros_ingresos}
          detalle={detalles.otrosIngresosDetalle}
          onUpdate={(amt, det) => handleUpdateIncome('otros_ingresos', amt, det)}
        />
      </div>

      {/* ── Summary ─────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <TrendingUp size={14} className="text-gray-400" />
          <span className="text-sm font-semibold text-gray-700">Resumen de Ingresos</span>
        </div>
        <table className="w-full">
          <tbody>
            {INCOME_ROWS.map(({ key, label }) => (
              <tr key={key} className="border-t border-gray-100 first:border-t-0">
                <td className="px-4 py-2 text-sm text-gray-500">{label}</td>
                <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                  RD$ {fmt(incomes[key])}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t-2 border-gray-200">
              <td className="px-4 py-3 text-sm font-bold text-gray-900">Total</td>
              <td className="px-4 py-3 text-base font-bold text-blue-600 text-right">
                RD$ {fmt(totalIngresos)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

    </div>
  );
}
